import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import type { ReactNode } from 'react';

// Keep in sync with src/beetle_forms_console/client/src/utils/sanitizeHtml.ts
const ALLOWED_TAGS = [
  'b',
  'i',
  'em',
  'strong',
  'u',
  'br',
  'p',
  'span',
  'div',
  'ul',
  'ol',
  'li',
  'a',
  'img',
];
const ALLOWED_ATTR = ['style', 'dir', 'href', 'src', 'alt'];
const FONT_SIZE_STYLE_RE = /font-size:\s*\d+(\.\d+)?(pt|px|em|rem)/gi;

let styleSanitizerHookRegistered = false;

function registerStyleSanitizerHook(): void {
  if (styleSanitizerHookRegistered) {
    return;
  }
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'style') {
      return;
    }
    const fontSizeMatches = data.attrValue.match(FONT_SIZE_STYLE_RE);
    data.attrValue = fontSizeMatches ? fontSizeMatches.join('; ') : '';
  });
  styleSanitizerHookRegistered = true;
}

registerStyleSanitizerHook();

/**
 * Sanitize HTML string. Use with renderSanitizedHtml() to avoid dangerouslySetInnerHTML.
 */
function sanitizeHtml(html: string | undefined): string {
  if (html == null || html === '') {
    return '';
  }
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}

/**
 * Sanitize HTML and render as React elements. Avoids dangerouslySetInnerHTML entirely
 * (sanitize then parse to React nodes), satisfying XSS/SAST requirements.
 */
export function renderSanitizedHtml(html: string | undefined): ReactNode {
  const cleaned = sanitizeHtml(html);
  if (cleaned === '') {
    return null;
  }
  return parse(cleaned);
}

export { sanitizeHtml };
